import SlickCarousel from '@ant-design/react-slick';
import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { screenClassMaxWidths } from '../../constants/tokens-screen.js';
import { useId } from '../../hooks/use-id.js';
import { conditionalPropType } from '../../prop-types/conditional.prop-type.js';
import '../../prop-types/nullish.prop-types.js';
import '../../utilities/base-url.utils.js';
import { className } from '../../utilities/classnames.utils.js';
import '../../constants/domains.js';
import '../../constants/globals.js';
import '../../constants/tokens-color.js';
import '../../types/enums.js';
import '../../utilities/manifest-icons.utils.js';
import '../../utilities/manifest-js.utils.js';
import { assignRefs } from '../../utilities/ref.utils.js';
import { filterProps } from '../core/core.utils.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { processStyleProps } from '../core/swan-style.js';
import { Icon } from '../icon/icon.component.js';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
const hasDots = props => {
  return (props === null || props === void 0 ? void 0 : props.dots) || (props === null || props === void 0 ? void 0 : props.progressIndicator) === 'dots' || (props === null || props === void 0 ? void 0 : props.progressIndicator) === 'dots-inset';
};
const propTypes = {
  skin: PropTypes.oneOf(['standard', 'full', 'promo-bar']),
  progressIndicator: PropTypes.oneOf(['none', 'dots', 'dots-inset', 'fraction']),
  progressIndicatorAlignment: PropTypes.oneOf(['center', 'left']),
  peek: PropTypes.bool,
  gridGutters: PropTypes.bool,
  gridGuttersVariant: PropTypes.string,
  arrowVerticalPosition: PropTypes.string,
  arrowVerticalOffset: PropTypes.string,
  accessibleTextForPrevious: PropTypes.string.isRequired,
  accessibleTextForNext: PropTypes.string.isRequired,
  accessibleTextForDots: conditionalPropType(hasDots, PropTypes.arrayOf(PropTypes.string.isRequired).isRequired, PropTypes.arrayOf(PropTypes.string.isRequired)),
  currentSlide: PropTypes.number
};
const CarouselButton = _ref => {
  let {
    onClick,
    accessibleText,
    iconType,
    className,
    style
  } = _ref;
  return /*#__PURE__*/React.createElement("button", {
    onClick: onClick,
    className: className,
    style: style
  }, /*#__PURE__*/React.createElement(Icon, {
    alt: accessibleText,
    iconType: iconType,
    size: "16p"
  }));
};
const propKeysToRemove = ['role', 'aria-label', 'aria-labelledby', 'aria-roledescription', ...Object.keys(propTypes)];
const Carousel = renderWithRef('Carousel', propTypes, (props, ref) => {
  const classNameIter = processStyleProps(props);
  const filteredProps = filterProps(props, propKeysToRemove, classNameIter);
  const {
    as,
    component,
    skin,
    progressIndicator,
    progressIndicatorAlignment,
    peek,
    gridGutters,
    gridGuttersVariant,
    arrowVerticalPosition,
    arrowVerticalOffset = '0px',
    children,
    // we extract some of the slickCarouselProps in order to add necessary classes
    // the defaults that we set here should match the Slick documentation so that we don't confuse folks
    dots,
    arrows,
    variableWidth,
    centerMode,
    slidesToShow = 1,
    centerPadding,
    // Props for container div
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-roledescription': ariaRoleDescription,
    accessibleTextForPrevious,
    accessibleTextForNext,
    accessibleTextForDots,
    customPaging,
    currentSlide,
    initialSlide = currentSlide
  } = props;
  const autoplay = false // props.autoplay // AutoPlay Is Always set to false
  ;

  const [isInitialized, setIsInitialized] = React.useState(skin === 'promo-bar');
  const finalSlidesToShow = progressIndicator === 'fraction' ? 1 : slidesToShow;
  const id = useId();
  const placeholderClass = "swan-carousel-placeholder-".concat(id);
  const carouselRef = React.useRef();
  const combinedRef = assignRefs(ref, carouselRef);
  const dotsEnabled = dots || progressIndicator !== 'none' || !!customPaging;
  const arrowEnabled = arrows || progressIndicator === 'fraction';
  const finalProps = {
    prevArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      enabled: arrowEnabled,
      accessibleText: accessibleTextForPrevious,
      iconType: "chevronLeft"
    }),
    nextArrow: /*#__PURE__*/React.createElement(CarouselButton, {
      enabled: arrowEnabled,
      accessibleText: accessibleTextForNext,
      iconType: "chevronRight"
    }),
    arrows: arrowEnabled,
    centerMode: !!(centerMode || peek),
    dots: dotsEnabled,
    customPaging: dotsEnabled ? customPaging || (index => {
      const content = Array.isArray(accessibleTextForDots) && accessibleTextForDots[index] != undefined ? accessibleTextForDots[index] : index + 1;
      return progressIndicator === 'fraction' ? /*#__PURE__*/React.createElement("span", null, content) : /*#__PURE__*/React.createElement("button", null, content);
    }) : undefined,
    appendDots: dotsEnabled ? dots => {
      return /*#__PURE__*/React.createElement("ul", null, React.Children.map(dots, dot => /*#__PURE__*/React.isValidElement(dot) ? /*#__PURE__*/React.cloneElement(dot, {}, /*#__PURE__*/React.cloneElement(dot.props.children, {
        'aria-pressed': dot.props.className === 'slick-active'
      })) : dot));
    } : undefined,
    initialSlide: typeof initialSlide === 'number' ? initialSlide : peek && finalSlidesToShow > 1 ? 1 : 0,
    slidesToShow: finalSlidesToShow,
    autoplay,
    pauseOnHover: autoplay,
    pauseOnFocus: autoplay,
    pauseOnDotsHover: autoplay,
    variableWidth
  };
  const finalPropsXS = {
    arrows,
    autoplay: false
  };
  if (centerPadding !== undefined) {
    finalProps.centerPadding = centerPadding;
    finalPropsXS.centerPadding = centerPadding;
  }
  if (progressIndicator === 'fraction' && peek) {
    finalProps.centerPadding = '70';
  }
  if (peek) {
    finalPropsXS.centerPadding = '8';
  }
  warning(props.__dangerouslySuppressWarning || !props.autoplay, 'Carousel autoplay is currently disabled, pending future accessibility support');
  warning(dotsEnabled || arrowEnabled, 'At least one of dots or arrow must be enabled for a carousel');
  warning(as === undefined && component === undefined, 'Carousel does not support the `component` or `as` prop because it must render a third-party component');
  const styleClasses = className(filteredProps.className, 'swan-carousel', {
    ["swan-carousel-skin-".concat(skin)]: skin !== 'standard',
    'swan-carousel-dots': finalProps.dots,
    'swan-carousel-dots-left': finalProps.dots && progressIndicatorAlignment === 'left',
    'swan-carousel-dots-fraction': progressIndicator === 'fraction',
    'swan-carousel-dots-inset': progressIndicator === 'dots-inset',
    'swan-carousel-hide-arrows': !finalProps.arrows,
    'swan-carousel-variable-width': finalProps.variableWidth,
    'swan-carousel-grid-gutters': gridGutters,
    'swan-carousel-grid-gutters-tight': gridGutters && gridGuttersVariant === 'tight',
    'swan-carousel-placeholder': !isInitialized,
    ['swan-carousel-peek-non-focusable-' + slidesToShow]: !!peek && slidesToShow % 2 === 0,
    [placeholderClass]: !isInitialized
  });
  const containerStyle = {
    '--swan-carousel-arrow-top': arrowVerticalPosition,
    '--swan-carousel-arrow-vertical-offset': arrowVerticalOffset
  };
  React.useEffect(() => {
    setIsInitialized(true);
  }, []);
  React.useEffect(() => {
    if (typeof currentSlide === 'number' && carouselRef.current) {
      carouselRef.current.slickGoTo(currentSlide);
    }
  }, [currentSlide]);
  return /*#__PURE__*/React.createElement("div", {
    className: "swan-carousel-container",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-roledescription": ariaRoleDescription,
    role: role,
    style: containerStyle
  }, isInitialized ? /*#__PURE__*/React.createElement(SlickCarousel, _extends({
    responsive: [{
      breakpoint: screenClassMaxWidths.xs,
      settings: finalPropsXS
    }]
  }, filteredProps, finalProps, {
    ref: combinedRef,
    className: styleClasses
  }, {
    children
  })) : /*#__PURE__*/React.createElement("div", {
    className: styleClasses
  }, /*#__PURE__*/React.createElement("style", {
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML: {
      __html: "\n                .".concat(placeholderClass, " .slick-slide:nth-of-type(").concat(slidesToShow, ") ~ .slick-slide  {\n                  display: none !important;\n                }\n              ")
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "slick-list"
  }, /*#__PURE__*/React.createElement("div", {
    className: "slick-track"
  }, React.Children.map(children, slide => /*#__PURE__*/React.createElement("div", {
    className: "slick-slide"
  }, /*#__PURE__*/React.createElement("div", null, slide)))))));
});
Carousel.defaultProps = {
  skin: 'standard',
  progressIndicator: 'none',
  peek: false,
  gridGutters: false,
  gridGuttersVariant: 'standard',
  dots: false,
  arrows: true,
  variableWidth: false,
  centerMode: false,
  slidesToShow: 1,
  role: 'region',
  'aria-roledescription': 'carousel'
};
export { Carousel };